/**
 * Harry Boy: välj rätt-resultat-rad utifrån de synliga odds-raderna (samma logik som backend parse).
 * Målsnålt = minst antal mål totalt bland alla sparade rader; Målrikt = flest mål bland de fem första
 * (samma ordning som i listan = mest sannolika enligt odds); Säkert = lägst odds.
 *
 * Per match: (1) lägesblandning — ibland ett av de andra lägena; (2) zon-slump — ibland närliggande rad.
 */
export const HARRY_BOY_RICH_LINE_COUNT = 5;
/** Antal rätt-resultat-kolumner som visas i matchoddstabellen (backend/Harry Boy använder fortfarande 5 rader). */
export const CS_DISPLAY_ROW_COUNT = 4;

/** Andel matcher där rad väljs ur zon-poolen i stället för canonical. */
export const ZONE_JITTER_FRACTION = 0.35;

const ZONE_WEIGHTS = [0.5, 0.3, 0.2];

/** Primärläge + jämn fördelning mellan de två andra (per knapp). */
const MODE_BLEND = {
  tight: { tight: 0.7, rich: 0.15, safe: 0.15 },
  rich: { rich: 0.55, tight: 0.225, safe: 0.225 },
  safe: { safe: 0.7, tight: 0.15, rich: 0.15 },
};

/** @returns {{ h: number, a: number, sum: number } | null} */
export function parseScoreLabelForBet(label) {
  if (label == null) return null;
  let s = String(label).trim().replace(/\s+/g, "");
  for (const ch of ["–", "—", "∶", ":"]) {
    s = s.replace(ch, "-");
  }
  const m = s.match(/^(\d{1,2})-(\d{1,2})$/);
  if (!m) return null;
  const h = Number(m[1]);
  const a = Number(m[2]);
  if (h > 20 || a > 20 || Number.isNaN(h) || Number.isNaN(a)) return null;
  return { h, a, sum: h + a };
}

/** Lägst relevanta odds på raden (snitt om finns, annars min av bolagen). */
export function minOddsOnCsRow(row) {
  if (!row || typeof row !== "object") return null;
  if (row.avg != null && row.avg !== "") {
    const v = Number(row.avg);
    return Number.isFinite(v) ? v : null;
  }
  const a = row.svenskaspel_se != null ? Number(row.svenskaspel_se) : null;
  const b = row.unibet_se != null ? Number(row.unibet_se) : null;
  const fa = a != null && Number.isFinite(a) ? a : null;
  const fb = b != null && Number.isFinite(b) ? b : null;
  if (fa != null && fb != null) return Math.min(fa, fb);
  return fa ?? fb ?? null;
}

/** Högsta odds på raden (för tie-break vid målrikt). */
export function maxOddsOnCsRow(row) {
  if (!row || typeof row !== "object") return null;
  const vals = [];
  if (row.avg != null && row.avg !== "") {
    const v = Number(row.avg);
    if (Number.isFinite(v)) vals.push(v);
  }
  if (row.svenskaspel_se != null) {
    const v = Number(row.svenskaspel_se);
    if (Number.isFinite(v)) vals.push(v);
  }
  if (row.unibet_se != null) {
    const v = Number(row.unibet_se);
    if (Number.isFinite(v)) vals.push(v);
  }
  if (!vals.length) return null;
  return Math.max(...vals);
}

function weightedPick(weights) {
  const entries = Object.entries(weights);
  const u = Math.random();
  let acc = 0;
  for (const [key, w] of entries) {
    acc += w;
    if (u < acc) return key;
  }
  return entries[entries.length - 1][0];
}

function pickFromWeightedPool(pool) {
  if (!pool.length) return null;
  const total = pool.reduce((s, x) => s + x.weight, 0);
  if (total <= 0) return pool[0].row;
  const u = Math.random() * total;
  let acc = 0;
  for (const { row, weight } of pool) {
    acc += weight;
    if (u < acc) return row;
  }
  return pool[pool.length - 1].row;
}

function dedupeRowsByLabel(items) {
  const seen = new Set();
  const out = [];
  for (const x of items) {
    const lbl = String(x.r?.label ?? "").trim();
    if (!lbl || seen.has(lbl)) continue;
    seen.add(lbl);
    out.push(x);
  }
  return out;
}

function parsedRows(topScores) {
  return (Array.isArray(topScores) ? topScores : [])
    .map((r) => ({ r, p: parseScoreLabelForBet(r?.label) }))
    .filter((x) => x.p != null);
}

/** Likformigt ~33 % vardera: målsnålt, målrikt, säkert (per anrop). */
export function pickRandomMixSubmode() {
  const u = Math.random();
  if (u < 1 / 3) return "tight";
  if (u < 2 / 3) return "rich";
  return "safe";
}

/**
 * Lager 1: effektivt läge för en match (mix = ren slump; övrigt = primär + ibland annat läge).
 * @param {"tight" | "rich" | "safe" | "mix"} buttonMode
 * @returns {"tight" | "rich" | "safe"}
 */
export function resolveHarryBoyMode(buttonMode) {
  if (buttonMode === "mix") return pickRandomMixSubmode();
  const weights = MODE_BLEND[buttonMode];
  if (!weights) return buttonMode;
  return weightedPick(weights);
}

/**
 * @param {Array<{ label?: string }>} topScores
 * @param {"tight" | "rich" | "safe"} mode
 * @returns {Array<{ row: object, weight: number }>}
 */
export function buildHarryBoyZonePool(topScores, mode) {
  const rows = parsedRows(topScores);
  if (!rows.length) return [];

  let sorted = rows;
  if (mode === "tight") {
    sorted = [...rows].sort((a, b) => {
      if (a.p.sum !== b.p.sum) return a.p.sum - b.p.sum;
      const oa = minOddsOnCsRow(a.r) ?? Infinity;
      const ob = minOddsOnCsRow(b.r) ?? Infinity;
      return oa - ob;
    });
  } else if (mode === "rich") {
    const pool = rows.slice(0, Math.min(HARRY_BOY_RICH_LINE_COUNT, rows.length));
    sorted = [...pool].sort((a, b) => {
      if (a.p.sum !== b.p.sum) return b.p.sum - a.p.sum;
      const oa = maxOddsOnCsRow(a.r) ?? -Infinity;
      const ob = maxOddsOnCsRow(b.r) ?? -Infinity;
      return ob - oa;
    });
  } else if (mode === "safe") {
    sorted = [...rows].sort((a, b) => {
      const oa = minOddsOnCsRow(a.r) ?? Infinity;
      const ob = minOddsOnCsRow(b.r) ?? Infinity;
      if (oa !== ob) return oa - ob;
      return a.p.sum - b.p.sum;
    });
  }

  return dedupeRowsByLabel(sorted.slice(0, 3)).map((x, i) => ({
    row: x.r,
    weight: ZONE_WEIGHTS[i] ?? ZONE_WEIGHTS[ZONE_WEIGHTS.length - 1],
  }));
}

/**
 * @param {Array<{ label?: string }>} topScores
 * @param {"tight" | "rich" | "safe"} mode
 * @returns {object | null}
 */
export function pickHarryBoyRowCanonical(topScores, mode) {
  const rows = parsedRows(topScores);
  if (!rows.length) return null;

  if (mode === "tight") {
    let best = rows[0];
    for (const x of rows.slice(1)) {
      if (x.p.sum < best.p.sum) best = x;
      else if (x.p.sum === best.p.sum) {
        const ox = minOddsOnCsRow(x.r);
        const ob = minOddsOnCsRow(best.r);
        if (ox != null && ob != null && ox < ob) best = x;
      }
    }
    return best.r;
  }

  if (mode === "rich") {
    const pool = rows.slice(0, Math.min(HARRY_BOY_RICH_LINE_COUNT, rows.length));
    if (!pool.length) return null;
    let best = pool[0];
    for (const x of pool.slice(1)) {
      if (x.p.sum > best.p.sum) best = x;
      else if (x.p.sum === best.p.sum) {
        const ox = maxOddsOnCsRow(x.r);
        const ob = maxOddsOnCsRow(best.r);
        if (ox != null && ob != null && ox > ob) best = x;
      }
    }
    return best.r;
  }

  if (mode === "safe") {
    let best = rows[0];
    let bestOdd = minOddsOnCsRow(best.r);
    for (const x of rows.slice(1)) {
      const o = minOddsOnCsRow(x.r);
      if (o == null) continue;
      if (bestOdd == null || o < bestOdd) {
        best = x;
        bestOdd = o;
      } else if (bestOdd === o) {
        if (x.p.sum < best.p.sum) best = x;
      }
    }
    return best.r;
  }

  return null;
}

/**
 * @param {Array<{ label?: string }>} topScores
 * @param {"tight" | "rich" | "safe" | "mix"} buttonMode
 * @returns {object | null}
 */
export function pickHarryBoyRow(topScores, buttonMode) {
  const mode = resolveHarryBoyMode(buttonMode);
  if (Math.random() < ZONE_JITTER_FRACTION) {
    const pool = buildHarryBoyZonePool(topScores, mode);
    const row = pickFromWeightedPool(pool);
    if (row) return row;
  }
  return pickHarryBoyRowCanonical(topScores, mode);
}
